import { ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import {
  Box,
  Center,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Skeleton,
  Spinner,
  VStack,
} from '@chakra-ui/react';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useCallback, useEffect, useState } from 'react';

import Button from 'components/Button';
import Icon from 'components/Icon';
import { ProtectedElement } from 'components/ProtectedElement';
import Text from 'components/Text';

import DevSandboxTooltip from '../../DevSandboxTooltip';
import { IProjectCard } from '../../types';

interface IGithubAsset {
  name: string;
  download_url: string;
}

interface IDesignAssetsTabProps {
  designScreenshotUrl?: string | null;
  entityType?: string;
  entityScreenName?: string;
  projectCards?: IProjectCard[];
  isDownloadingFigma?: boolean;
  onFigmaDownload?: () => void;
  githubLink?: string;
}

const DesignAssetsTab = ({
  designScreenshotUrl,
  entityType,
  entityScreenName,
  projectCards,
  isDownloadingFigma,
  onFigmaDownload,
  githubLink,
}: IDesignAssetsTabProps) => {
  const { toastSuccess } = useToastWrapper();
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [githubAssets, setGithubAssets] = useState<IGithubAsset[]>([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);

  const cardsWithScreenshots = projectCards?.filter((c) => c.screenshot_url) || [];
  const isProject = entityType === 'project' && cardsWithScreenshots.length > 0;
  const currentScreenshot = isProject
    ? cardsWithScreenshots[currentCardIndex]?.screenshot_url
    : designScreenshotUrl;
  const currentCardTitle = isProject ? cardsWithScreenshots[currentCardIndex]?.title : null;

  // Fetch project assets from GitHub
  useEffect(() => {
    const fetchGithubAssets = async () => {
      if (entityType !== 'project' || !entityScreenName) return;

      setIsLoadingAssets(true);
      try {
        const response = await fetch(
          `https://api.github.com/repos/elise-bigdevsoon/big-dev-soon-${entityScreenName}/contents/assets`
        );
        if (!response.ok) {
          setGithubAssets([]);
          return;
        }
        const files = await response.json();
        if (!Array.isArray(files)) {
          setGithubAssets([]);
          return;
        }

        const imageAssets: IGithubAsset[] = files
          .filter((file: any) => /\.(png|jpg|jpeg|svg|gif|webp|ico)$/i.test(file.name))
          .map((file: any) => ({
            name: file.name,
            download_url: file.download_url,
          }));

        setGithubAssets(imageAssets);
      } catch {
        setGithubAssets([]);
      } finally {
        setIsLoadingAssets(false);
      }
    };

    fetchGithubAssets();
  }, [entityType, entityScreenName]);

  const handleCopyUrl = useCallback(
    (url?: string | null) => {
      if (url) {
        navigator.clipboard.writeText(url);
        toastSuccess('URL copied to clipboard!');
      }
    },
    [toastSuccess]
  );

  const handlePrevCard = useCallback(() => {
    if (isProject && currentCardIndex > 0) {
      setCurrentCardIndex((prev) => prev - 1);
    }
  }, [isProject, currentCardIndex]);

  const handleNextCard = useCallback(() => {
    if (isProject && currentCardIndex < cardsWithScreenshots.length - 1) {
      setCurrentCardIndex((prev) => prev + 1);
    }
  }, [isProject, cardsWithScreenshots.length, currentCardIndex]);

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <HStack justifyContent="space-between" alignItems="center" mb={2}>
          <HStack spacing={2}>
            <Text fontSize="large" fontWeight="semibold">
              Design Reference
            </Text>
            {currentScreenshot && (
              <DevSandboxTooltip label="Open full-size in new tab">
                <IconButton
                  aria-label="Open full-size in new tab"
                  icon={<ExternalLinkIcon />}
                  size="xs"
                  variant="ghost"
                  as="a"
                  href={currentScreenshot}
                  target="_blank"
                />
              </DevSandboxTooltip>
            )}
          </HStack>
        </HStack>
        <Text fontSize="small" opacity="secondary" mb={3}>
          {isProject
            ? 'Browse card designs. Use the overlay toggle in Browser tab (Cmd/Ctrl+Shift+D) for pixel-perfect comparison.'
            : 'Use this design as your reference. Toggle the overlay in the Browser tab (Cmd/Ctrl+Shift+D) for pixel-perfect comparison.'}
        </Text>
        {onFigmaDownload && (
          <ProtectedElement
            scheme="gradient"
            size="sm"
            leftIcon={isDownloadingFigma ? <Spinner size="xs" /> : <Icon type="FigmaLogo" />}
            fullWidth
            onClick={isDownloadingFigma ? undefined : onFigmaDownload}
          >
            {isDownloadingFigma ? 'Downloading...' : 'Download Figma Design'}
          </ProtectedElement>
        )}
      </Box>

      {/* Card navigation for projects */}
      {isProject && (
        <HStack justifyContent="space-between" alignItems="center" mb={3}>
          <HStack spacing={2}>
            <DevSandboxTooltip label="Previous card">
              <IconButton
                aria-label="Previous card"
                icon={<ChevronLeftIcon />}
                size="sm"
                variant="ghost"
                isDisabled={currentCardIndex === 0}
                onClick={handlePrevCard}
              />
            </DevSandboxTooltip>
            <Text fontSize="small" fontWeight="semibold">
              Card {currentCardIndex + 1} of {cardsWithScreenshots.length}
            </Text>
            <DevSandboxTooltip label="Next card">
              <IconButton
                aria-label="Next card"
                icon={<ChevronRightIcon />}
                size="sm"
                variant="ghost"
                isDisabled={currentCardIndex === cardsWithScreenshots.length - 1}
                onClick={handleNextCard}
              />
            </DevSandboxTooltip>
          </HStack>
          {currentCardTitle && (
            <Text fontSize="small" opacity="secondary" isTruncated maxW="200px">
              {currentCardTitle}
            </Text>
          )}
        </HStack>
      )}

      {/* Design screenshot */}
      {currentScreenshot ? (
        <Box
          position="relative"
          borderRadius="md"
          overflow="hidden"
          cursor="pointer"
          role="group"
          onClick={() => handleCopyUrl(currentScreenshot)}
          mb={4}
          minH="200px"
        >
          <Image
            src={currentScreenshot}
            alt={currentCardTitle || 'Design screenshot'}
            width="100%"
            height="auto"
            borderRadius="md"
            fallback={
              <Center height="200px" bg="whiteAlpha.100" borderRadius="md">
                <Text opacity="secondary">Image not found</Text>
              </Center>
            }
          />
          {/* Hover overlay */}
          <Center
            position="absolute"
            inset={0}
            bg="blackAlpha.600"
            opacity={0}
            _groupHover={{ opacity: 1 }}
            transition="opacity 0.15s"
          >
            <Box textAlign="center">
              <Icon type="Copy" />
              <Text fontSize="small" mt={2}>
                Click to copy URL
              </Text>
            </Box>
          </Center>
        </Box>
      ) : (
        <Center height="200px" bg="whiteAlpha.100" borderRadius="md" mb={4}>
          <Text opacity="secondary">No design screenshot available</Text>
        </Center>
      )}

      {/* Card thumbnails for quick navigation */}
      {isProject && cardsWithScreenshots.length > 1 && (
        <Box mb={4}>
          <Text fontSize="small" fontWeight="semibold" mb={2}>
            All Cards
          </Text>
          <HStack spacing={2} overflowX="auto" pb={2}>
            {cardsWithScreenshots.map((card, index) => (
              <Box
                key={card.id}
                width="60px"
                height="45px"
                borderRadius="sm"
                overflow="hidden"
                cursor="pointer"
                opacity={index === currentCardIndex ? 1 : 0.5}
                borderWidth="2px"
                borderColor={index === currentCardIndex ? 'purple.400' : 'transparent'}
                transition="all 0.15s"
                onClick={() => setCurrentCardIndex(index)}
                _hover={{ opacity: 1 }}
                flexShrink={0}
              >
                <Image
                  src={card.screenshot_url || undefined}
                  alt={card.title}
                  width="100%"
                  height="100%"
                  objectFit="cover"
                  fallback={
                    <Center height="100%" bg="whiteAlpha.100">
                      <Text fontSize="micro">{index + 1}</Text>
                    </Center>
                  }
                />
              </Box>
            ))}
          </HStack>
        </Box>
      )}

      {/* GitHub Project Assets */}
      {entityType === 'project' && (
        <Box mb={4}>
          <Text fontSize="small" fontWeight="semibold" mb={3}>
            Project Assets
          </Text>
          {isLoadingAssets ? (
            <SimpleGrid columns={4} spacing={2}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} height="60px" borderRadius="md" />
              ))}
            </SimpleGrid>
          ) : githubAssets.length > 0 ? (
            <SimpleGrid columns={4} spacing={2}>
              {githubAssets.map((asset) => (
                <Box
                  key={asset.name}
                  position="relative"
                  borderRadius="md"
                  overflow="hidden"
                  cursor="pointer"
                  role="group"
                  bg="whiteAlpha.100"
                  onClick={() => handleCopyUrl(asset.download_url)}
                  _hover={{ transform: 'scale(1.02)' }}
                  transition="all 0.15s"
                >
                  <Image
                    src={asset.download_url}
                    alt={asset.name}
                    width="100%"
                    height="60px"
                    objectFit="contain"
                    p={1}
                    fallback={
                      <Center height="60px">
                        <Text fontSize="micro" isTruncated px={1}>
                          {asset.name}
                        </Text>
                      </Center>
                    }
                  />
                  <Center
                    position="absolute"
                    inset={0}
                    bg="blackAlpha.700"
                    opacity={0}
                    _groupHover={{ opacity: 1 }}
                    transition="opacity 0.15s"
                  >
                    <VStack spacing={0}>
                      <Icon type="Copy" size={14} />
                      <Text fontSize="micro" mt={1}>
                        Copy URL
                      </Text>
                    </VStack>
                  </Center>
                </Box>
              ))}
            </SimpleGrid>
          ) : null}
        </Box>
      )}

      {/* Resources */}
      {githubLink && (
        <Box mb={4}>
          <Button
            href={githubLink}
            target="_blank"
            size="sm"
            scheme="outline"
            leftIcon={<Icon type="Github" size={16} />}
            width="100%"
            justifyContent="flex-start"
          >
            GitHub Template
          </Button>
        </Box>
      )}

      {/* Tips */}
      <Box p={4} bg="whiteAlpha.100" borderRadius="md">
        <Text fontSize="small" fontWeight="semibold" mb={2}>
          Tips for Matching the Design
        </Text>
        <Box as="ul" pl={4} fontSize="small" opacity="secondary">
          <Box as="li" mb={1}>
            Use the <strong>overlay toggle</strong> (Cmd/Ctrl+Shift+D) in Browser tab to compare your work
          </Box>
          <Box as="li" mb={1}>
            Adjust the <strong>opacity slider</strong> to see differences clearly
          </Box>
          <Box as="li" mb={1}>
            Check the <strong>Design System tab</strong> for extracted colors and tokens
          </Box>
          <Box as="li">
            Download the <strong>Figma file</strong> for exact measurements and assets
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DesignAssetsTab;
